import React from 'react';
import { ChatDayGroupedMessages } from 'src/pages/Conversations/Chat/chat-day-grouped-messages';
import { MessageItemSkeleton } from 'src/pages/Conversations/Chat/ChatMessage/chat-message';
import { ThreadColumn } from 'src/pages/Conversations/Chat/ChatMessage/day-section';
import { NewMessagesDivider } from 'src/pages/Conversations/Chat/ChatMessage/new-messages-divider';
import { useNewMessagesBoundary } from 'src/pages/Conversations/Chat/new-messages-boundary-context';
import { useChatCommonData } from './common-data-context';
import { useAllBlockingResolved, useChatPagination, useMergedTimeline } from './hooks';
import { messageRegistry } from './index';
import { RegistryRow } from './RegistryRow';
import { MessageTypeId } from './types';
import { usePrependScrollLock } from './use-prepend-scroll-lock';

const ChatThreadSkeleton = (): React.JSX.Element => (
  <ThreadColumn data-testid="chat-thread-skeleton">
    <MessageItemSkeleton />
  </ThreadColumn>
);

const ChatThread = (): React.JSX.Element => {
  const entries = useMergedTimeline();
  const { shouldRender } = useAllBlockingResolved();
  const { hasMore } = useChatPagination();
  const { boundaryKey, count, isDismissing, registerDivider } = useNewMessagesBoundary();
  const chatRef = useChatCommonData((s) => s.chatRef);

  /*
    Only chat messages paginate, so a prepend is detected off the oldest loaded
    MESSAGE entry (a group's timestamp is its oldest message's, so it also gets
    older when a page merges INTO the group). Entries are newest-first, and other
    types (a signal incident may be older than every message) are skipped.
  */
  const oldestMessageTimestamp =
    entries.findLast((entry) => entry.type === MessageTypeId.ChatMessage)?.timestamp ?? null;
  usePrependScrollLock(chatRef, oldestMessageTimestamp);

  if (!shouldRender) {
    return <ChatThreadSkeleton />;
  }

  return (
    <>
      <ChatDayGroupedMessages
        messageList={entries}
        renderMessage={(entry) => {
          const registration = messageRegistry[entry.type];
          if (!registration) return null;

          const key = `${entry.type}:${registration.getKey(entry.item)}`;

          /*
            The thread renders column-reverse, so the divider must follow its row in DOM
            order to appear above it. The fragment is unconditional so the row keeps its
            reconciliation identity when the boundary comes and goes.
          */
          return (
            <React.Fragment key={key}>
              <RegistryRow registration={registration} item={entry.item} />
              {key === boundaryKey ? (
                <NewMessagesDivider
                  ref={registerDivider}
                  count={count}
                  isDismissing={isDismissing}
                />
              ) : null}
            </React.Fragment>
          );
        }}
      />
      {/*
        Tied to "older history exists" rather than "a fetch is in flight": pages load a
        screenful early and often arrive back to back, so a load-scoped skeleton mounts
        and unmounts once per page — read as two skeletons swapping places right where
        the reader is looking. Keeping it mounted while hasMore holds means the top edge
        never changes shape during paging; it only goes away at the start of the thread.
      */}
      {hasMore ? <ChatThreadSkeleton /> : null}
    </>
  );
};

export default ChatThread;
